// src/pages/company-head/AppAccessLog.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import axios from 'axios';
import { 
  FaEye, FaSearch, FaFilter, FaCalendar, FaUser, FaMobile,
  FaClock, FaMapMarkerAlt, FaSignInAlt, FaSignOutAlt, FaChartLine
} from 'react-icons/fa';

const AppAccessLog = () => {
  const { user } = useContext(AuthContext);
  const [accessLogs, setAccessLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSessions: 0,
    activeDealers: 0,
    avgSessionDuration: 0,
    todaysLogins: 0
  });
  
  // Filters and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalLogs: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Get company ID
  const getCompanyId = () => {
    if (typeof user?.user?.company === 'string') {
      return user.user.company;
    }
    return user?.user?.company?._id || user?.user?.company?.id;
  };

  // Fetch access logs
  const fetchAccessLogs = async (page = 1, search = '', date = '', action = '') => {
    try {
      setLoading(true);
      const companyId = getCompanyId();
      
      if (!companyId) {
        console.error('No company ID found');
        return;
      }

      // Placeholder data since API endpoints would need to be created
      setTimeout(() => {
        setAccessLogs([
          {
            _id: '1',
            dealerId: 'AD00000001',
            dealerName: 'RAJESH TILES MART',
            action: 'login',
            timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            ipAddress: '192.168.1.100',
            deviceInfo: 'Android 12, Samsung Galaxy S21',
            location: 'Mumbai, Maharashtra',
            sessionDuration: 45
          },
          {
            _id: '2',
            dealerId: 'AD00000002',
            dealerName: 'CERAMIC WORLD',
            action: 'logout',
            timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
            ipAddress: '192.168.1.105',
            deviceInfo: 'iOS 16, iPhone 14',
            location: 'Delhi, NCR',
            sessionDuration: 67
          },
          {
            _id: '3',
            dealerId: 'AD00000001',
            dealerName: 'RAJESH TILES MART',
            action: 'profile_update',
            timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
            ipAddress: '192.168.1.100',
            deviceInfo: 'Android 12, Samsung Galaxy S21',
            location: 'Mumbai, Maharashtra',
            sessionDuration: null
          },
          {
            _id: '4',
            dealerId: 'AD00000003',
            dealerName: 'TILE PARADISE',
            action: 'login',
            timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
            ipAddress: '192.168.1.110',
            deviceInfo: 'Android 11, OnePlus 9',
            location: 'Bangalore, Karnataka',
            sessionDuration: 23
          },
          {
            _id: '5',
            dealerId: 'AD00000002',
            dealerName: 'CERAMIC WORLD',
            action: 'order_view',
            timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
            ipAddress: '192.168.1.105',
            deviceInfo: 'iOS 16, iPhone 14',
            location: 'Delhi, NCR',
            sessionDuration: null
          }
        ]);
        
        setStats({
          totalSessions: 156,
          activeDealers: 23,
          avgSessionDuration: 42,
          todaysLogins: 8
        });
        
        setPagination({
          totalPages: 1,
          totalLogs: 5,
          hasNextPage: false,
          hasPrevPage: false
        });
        
        setLoading(false);
      }, 1000);

    } catch (error) {
      console.error('Error fetching access logs:', error);
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAccessLogs(1, searchTerm, dateFilter, actionFilter);
  };

  // Handle action filter
  const handleActionFilter = (action) => {
    setActionFilter(action);
    setCurrentPage(1);
    fetchAccessLogs(1, searchTerm, dateFilter, action);
  };

  // Action badge component
  const ActionBadge = ({ action }) => {
    const getActionColor = (action) => {
      switch (action) {
        case 'login': return 'bg-green-100 text-green-800';
        case 'logout': return 'bg-red-100 text-red-800';
        case 'profile_update': return 'bg-blue-100 text-blue-800';
        case 'order_view': return 'bg-purple-100 text-purple-800';
        case 'order_create': return 'bg-orange-100 text-orange-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    const getActionIcon = (action) => {
      switch (action) {
        case 'login': return <FaSignInAlt className="mr-1" />;
        case 'logout': return <FaSignOutAlt className="mr-1" />;
        case 'profile_update': return <FaUser className="mr-1" />;
        case 'order_view': return <FaEye className="mr-1" />;
        case 'order_create': return <FaSignInAlt className="mr-1" />;
        default: return null;
      }
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(action)}`}>
        {getActionIcon(action)}
        {action.replace('_', ' ').charAt(0).toUpperCase() + action.replace('_', ' ').slice(1)}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  useEffect(() => {
    fetchAccessLogs();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600 mb-4" role="status"></div>
          <p className="text-gray-600">Loading access logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">App Access Log</h1>
        <p className="text-gray-600">Monitor dealer app usage and access patterns</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSessions}</p>
            </div>
            <FaChartLine className="text-blue-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Dealers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeDealers}</p>
            </div>
            <FaUser className="text-green-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Session</p>
              <p className="text-2xl font-bold text-gray-900">{stats.avgSessionDuration}m</p>
            </div>
            <FaClock className="text-purple-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Today's Logins</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todaysLogins}</p>
            </div>
            <FaSignInAlt className="text-orange-500 text-2xl" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by dealer name, ID, or IP address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </form>

          {/* Date Filter */}
          <div className="relative">
            <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Action Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => handleActionFilter('')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                actionFilter === '' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => handleActionFilter('login')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                actionFilter === 'login' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Logins
            </button>
            <button
              onClick={() => handleActionFilter('logout')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                actionFilter === 'logout' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Logouts
            </button>
          </div>
        </div>
      </div>

      {/* Access Logs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dealer Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Device & Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {accessLogs.map((log) => (
                <tr key={log._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                          {log.dealerName.charAt(0)}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{log.dealerName}</div>
                        <div className="text-sm text-gray-500">ID: {log.dealerId}</div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ActionBadge action={log.action} />
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <FaClock className="mr-2 text-gray-400" />
                      {formatDate(log.timestamp)}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      <div className="flex items-center mb-1">
                        <FaMobile className="mr-2 text-gray-400" />
                        <span className="truncate max-w-48">{log.deviceInfo}</span>
                      </div>
                      <div className="flex items-center mb-1">
                        <FaMapMarkerAlt className="mr-2 text-gray-400" />
                        {log.location}
                      </div>
                      <div className="text-xs text-gray-500">
                        IP: {log.ipAddress}
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDuration(log.sessionDuration)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {accessLogs.length === 0 && (
          <div className="text-center py-12">
            <FaEye className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No access logs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Access logs will appear here as dealers use the mobile app.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppAccessLog;
